import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#0d47a1';

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDefaultFromDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
};

const fmtAmt = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};

const ProfitLossMonthWise = () => {
  useSessionValidation();
  const { companyId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(fmt2Date(new Date()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('0');

  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('BranchApi', `/${companyId}`, token)
      .then(res => {
        if (res.responseCode === '1111') setBranches(res.response_data || []);
        else setBranches(res.response_data || res || []);
      })
      .catch(() => setBranches([]));
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
        if (new Date(fromDate) > new Date(toDate)) {
            setData([]);
            setError('From Date cannot be greater than To Date');
            return;
        }
        fetchReport();
    }
  }, [companyId, token, fromDate, toDate, selectedBranch]); // eslint-disable-line

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const branchParam = selectedBranch === '0' ? 0 : parseInt(selectedBranch);
      const res = await fetchData(
        'MISReportApi',
        `/ProfitAndLossMonthlyWise/${fromDate}/${toDate}/${companyId}/${branchParam}`,
        token
      );
      setData(res.response_data || res || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const income = data.filter(r => (r.Side || '').toLowerCase() === 'income')
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
  const expenditure = data.filter(r => (r.Side || '').toLowerCase() === 'expenditure')
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));

  const totalIncome = income.reduce((s, r) => s + (parseFloat(r.Amount) || 0), 0);
  const totalExpenditure = expenditure.reduce((s, r) => s + (parseFloat(r.Amount) || 0), 0);
  const netProfit = totalIncome - totalExpenditure;
  const isProfit = netProfit >= 0;
  const maxRows = Math.max(income.length, expenditure.length);

  const selectedBranchObj = branches.find(b => String(b.Id) === String(selectedBranch));
  const selectedBranchName = selectedBranchObj ? (selectedBranchObj.Name || selectedBranchObj.BranchName || '') : '';

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ProfitLossMonthWise', 0);
    if (!data.length) return;
    const rows = [
      ['Expenditure', '', 'Income', ''],
      ['Particulars', 'Debit (₹)', 'Particulars', 'Credit (₹)'],
    ];
    for (let i = 0; i < maxRows; i++) {
      const exp = expenditure[i];
      const inc = income[i];
      rows.push([
        exp ? exp.Particular : '', exp ? fmtAmt(exp.Amount) : '',
        inc ? inc.Particular : '', inc ? fmtAmt(inc.Amount) : '',
      ]);
    }
    rows.push([]);
    rows.push(['Total Expenditure', fmtAmt(totalExpenditure), 'Total Income', fmtAmt(totalIncome)]);
    if (isProfit) rows.push(['Net Profit', fmtAmt(netProfit), '', '']);
    else rows.push(['', '', 'Net Loss', fmtAmt(Math.abs(netProfit))]);

    await exportStyledExcel({
      reportTitle: 'PROFIT & LOSS STATEMENT — MONTH WISE',
      sheetName: 'P&L Month Wise',
      fileName: `ProfitLoss_MonthWise_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows: rows,
      columnWidths: [32, 16, 32, 16],
    });
  };

  const cell = (align = 'left', bold = false, color = '#1e293b', bg = '#fff') => ({
    padding: '6px 10px', fontSize: 12, textAlign: align,
    fontWeight: bold ? 700 : 400, color, background: bg,
    borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
  });

  const th = (align = 'left', bg = '#f8fafc') => ({
    padding: '7px 10px', fontSize: 11, textAlign: align, fontWeight: 700,
    color: '#64748b', background: bg, borderBottom: '2px solid #e2e8f0',
    textTransform: 'uppercase', letterSpacing: 0.3,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', padding: 12, fontFamily: "'Inter', sans-serif" }}>
      {loading && <LogoLoader message="Loading report..." />}

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1565c0 100%)`, gap: 8, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
            Profit &amp; Loss Statement
          </span>
          <div style={{ flex: 1 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, background: 'rgba(255,255,255,0.15)', color: '#fff', colorScheme: 'dark', minWidth: 120 }}>
            <option value="0" style={{ background: '#1e40af', color: '#fff' }}>All Branches</option>
            {branches.map(b => (
              <option key={b.Id} value={String(b.Id)} style={{ background: '#1e40af', color: '#fff' }}>
                {b.Name || b.BranchName}
              </option>
            ))}
          </select>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Report body */}
        <div style={{ padding: 16, overflowX: 'auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: PRIMARY }}>{companyName}</div>
            {selectedBranch === '0'
              ? <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>All Branches</div>
              : selectedBranchName
                ? <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>{selectedBranchName}</div>
                : null
            }
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Profit &amp; Loss Statement — Month Wise &nbsp;|&nbsp; {fromDate} to {toDate}
            </div>
          </div>

          {!loading && data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
              No data found for the selected period.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '5px' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={2} style={{ ...th('center', '#fef2f2'), color: '#b91c1c', fontSize: 12, borderBottom: '2px solid #fecaca' }}>
                    Expenditure
                  </th>
                  <td style={{ background: '#e2e8f0' }} />
                  <th colSpan={2} style={{ ...th('center', '#f0fdf4'), color: '#15803d', fontSize: 12, borderBottom: '2px solid #bbf7d0' }}>
                    Income
                  </th>
                </tr>
                <tr>
                  <th style={th('left', '#fef2f2')}>Particulars</th>
                  <th style={th('right', '#fef2f2')}>Debit</th>
                  <td style={{ background: '#e2e8f0' }} />
                  <th style={th('left', '#f0fdf4')}>Particulars</th>
                  <th style={th('right', '#f0fdf4')}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxRows }).map((_, i) => {
                  const exp = expenditure[i];
                  const inc = income[i];
                  return (
                    <tr key={i}>
                      <td style={cell('left', false, '#374151', i % 2 === 0 ? '#fff' : '#fef9f9')}>
                        {exp ? exp.Particular : ''}
                      </td>
                      <td style={cell('right', false, '#b91c1c', i % 2 === 0 ? '#fff' : '#fef9f9')}>
                        {exp ? fmtAmt(exp.Amount) : ''}
                      </td>
                      <td style={{ background: '#e2e8f0', width: 5 }} />
                      <td style={cell('left', false, '#374151', i % 2 === 0 ? '#fff' : '#f0fdf9')}>
                        {inc ? inc.Particular : ''}
                      </td>
                      <td style={cell('right', false, '#15803d', i % 2 === 0 ? '#fff' : '#f0fdf9')}>
                        {inc ? fmtAmt(inc.Amount) : ''}
                      </td>
                    </tr>
                  );
                })}

                {/* Net row */}
                {isProfit ? (
                  // Income > Expenditure → Net Profit shown on Expenditure side
                  <tr style={{ background: '#d1fae5' }}>
                    <td style={cell('right', true, '#065f46', '#d1fae5')}>Net Profit</td>
                    <td style={cell('right', true, '#065f46', '#d1fae5')}>{fmtAmt(netProfit)}</td>
                    <td style={{ background: '#e2e8f0' }} />
                    <td style={cell('left', false, '#065f46', '#d1fae5')} />
                    <td style={cell('right', false, '#065f46', '#d1fae5')} />
                  </tr>
                ) : (
                  // Expenditure > Income → Net Loss shown on Income side
                  <tr style={{ background: '#fee2e2' }}>
                    <td style={cell('left', false, '#991b1b', '#fee2e2')} />
                    <td style={cell('right', false, '#991b1b', '#fee2e2')} />
                    <td style={{ background: '#e2e8f0' }} />
                    <td style={cell('right', true, '#991b1b', '#fee2e2')}>Net Loss</td>
                    <td style={cell('right', true, '#991b1b', '#fee2e2')}>{fmtAmt(Math.abs(netProfit))}</td>
                  </tr>
                )}

                {/* Totals — each side shows its own actual total */}
                <tr style={{ background: '#f1f5f9' }}>
                  <td style={cell('right', true, '#1e293b', '#f1f5f9')}>Total Expenditure</td>
                  <td style={cell('right', true, '#b91c1c', '#f1f5f9')}>
                    {fmtAmt(totalExpenditure)}
                  </td>
                  <td style={{ background: '#e2e8f0' }} />
                  <td style={cell('right', true, '#1e293b', '#f1f5f9')}>Total Income</td>
                  <td style={cell('right', true, '#15803d', '#f1f5f9')}>
                    {fmtAmt(totalIncome)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default ProfitLossMonthWise;
